"use client";

import React, { useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useConsultation } from "../../../../context/ConsultationContext";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import Table from "../../../../components/Table.jsx";
import styles from "../css/ServicesDone.module.css";

import { getPatientServiceSummary } from "../../../../api/clinic/consultationApi.js";
import { getConsultationHistoryPatient } from "../../../../api/clinic/consultationApi.js";
import MedicalHistoryHeader from "../components/MedicalHistoryHeader";
import { getProfileByPatient } from "../../../../api/profile/create_profile.js";

export default function ServicesDone() {
  const { id } = useParams();
  const { consultationData } = useConsultation();
  const [services, setServices] = useState([]);
  const [patient, setPatient] = useState(consultationData?.patient || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [profileData, setProfileData] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError("");
      
      const patientId = id || consultationData?.patient?.patientid;
      
      if (!patientId) {
        if (!mounted) return;
        setError("No patient ID provided");
        setLoading(false);
        return;
      }
      
      try {
        const [svc, p0] = await Promise.all([
          getPatientServiceSummary(patientId),
          consultationData?.patient ? Promise.resolve(consultationData.patient) : getConsultationHistoryPatient(patientId),
        ]);
        if (!mounted) return;
        setServices(Array.isArray(svc) ? svc : []);

        let p = p0;
        if (!p) {
          const { getPatientConsultationTimeline } = await import("../../../../api/clinic/consultationApi.js");
          const timeline = await getPatientConsultationTimeline(patientId);
          p = Array.isArray(timeline) && timeline.length ? timeline[0]?.patient : null;
        }
        setPatient(p);
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || "Failed to load services");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id, consultationData]);

  useEffect(() => {
    const patientId = id || consultationData?.patient?.patientid;
    if (!patientId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getProfileByPatient(patientId);
        if (!cancelled) setProfileData(data);
      } catch {
        if (!cancelled) setProfileData(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, consultationData?.patient?.patientid]);

  const patientFromProfile = profileData?.patient || null;
  const resolvedPatient = {
    ...(patientFromProfile || patient || {}),
    profilenumber:
      profileData?.profile?.profilenumber ??
      patientFromProfile?.profilenumber ??
      patient?.profilenumber,
    medicalaidprovider: profileData?.mainmember?.medicalaidprovider ?? profileData?.mainmember?.medicalAidProvider,
    medicalaidnumber: profileData?.mainmember?.medicalaidnumber ?? profileData?.mainmember?.medicalAidNumber,
  };

  const maxCount = useMemo(() => {
    if (!services.length) return 0;
    return Math.max(...services.map((s) => s.visits || 0));
  }, [services]);

  const tableData = useMemo(() => {
    return services.map((s) => ({
      servicename: s.servicename || "",
      visits: s.visits || 0,
    }));
  }, [services]);

  const columns = [
    { header: "Service Name", accessor: "servicename" },
    { header: "Service Count", accessor: "visits" },
  ];

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      <main className={styles.mainContent}>
        <div className={styles.container}>
          <MedicalHistoryHeader title="Services Done" patient={resolvedPatient} />
          <div className={styles.header}>
            <h2 className={styles.title}>Services Done</h2>
            <p className={styles.subtitle}>
              Clinic services performed for {(resolvedPatient?.firstname || "").trim() || "Patient"}
            </p>
          </div>

          {error && <div className={styles.inlineError}>{error}</div>}

          {loading ? (
            <div className={styles.loadingMessage}>Loading...</div>
          ) : (
            <>
              <div className={styles.chartCard}>
                <div className={styles.chartContainer}>
                  {services.map((service, index) => {
                    const count = service.visits || 0;
                    const widthPercent =
                      maxCount > 0 ? (count / maxCount) * 100 : 0;
                    return (
                      <div key={service.serviceid || index} className={styles.barRow}>
                        <div className={styles.barLabel}>{service.servicename}</div>
                        <div className={styles.barValue}>{count}</div>
                        <div className={styles.barTrack}>
                          <div
                            className={styles.barFill}
                            style={{ width: `${widthPercent}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className={styles.tableCard}>
                <Table columns={columns} data={tableData} />
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
